import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
  IEntityUser,
  IGenericRole,
  IRole,
  IRoleSetViewModel,
  IRolesViewModel,
  IUser,
  IUserEntity,
  RoleAuthTarget,
} from '@boma/client-shared';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  distinctUntilChanged,
  Observable,
  switchMap,
} from 'rxjs';
import {
  applyUserRoles,
  getEntityRoles,
  getLocationRoles,
  getSelectedUser,
  getUserEntity,
  getVehicleRoles,
  loadAssignedAdminRoles,
  loadRoles,
  RolesFacade,
  updateUserEntity,
  UsersFacade,
} from '@boma/supplier-portal/users';
import { ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { ILocation } from '@boma/supplier-portal/shared/entities';
import { getUser } from '@boma/supplier-portal/shared/state';
import { BreadcrumbService } from '@boma/ui';
import { map, tap } from 'rxjs/operators';
import {
  HelperFunctionsService,
  LocationsService,
} from '@boma/supplier-portal/shared/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash';

@Component({
  selector: 'boma-details',
  templateUrl: './details.component.html',
  styleUrls: ['./details.component.scss'],
})
export class DetailsComponent implements OnInit {
  readonly destroyRef = inject(DestroyRef);

  userId: string;
  entityId: string;
  loading$: Observable<boolean>;
  //TODO why so many user entity things
  loggedInUser$: Observable<IUser>;
  user$: Observable<IUserEntity>; // The currently logged in user - we need to extract the supplier entity from this user

  userEntity$: Observable<IEntityUser>;

  entityRoleOptions$: Observable<IRole[]>;
  locationRoleOptions$: Observable<IRole[]>;
  vehicleRoleOptions$: Observable<IRole[]>;

  assignedLocationRolesDiff: IRoleSetViewModel[] = [];
  originalAssignedLocationRolesDiff = [];
  assignedVehicleRolesDiff: IRoleSetViewModel[] = [];
  originalAssignedVehicleRolesDiff = [];
  assignedAdminRolesDiff: IRolesViewModel[] = [];
  assignedCustomAdminRoles: IRolesViewModel[] = [];

  updateFormGroup: FormGroup = new FormGroup({
    identificationNumber: new FormControl(''),
    employeeNumber: new FormControl(''),
    position: new FormControl(''),
    companyContactNumber: new FormControl(''),
    personalContactNumber: new FormControl(''),
    department: new FormControl(''),
    notes: new FormControl(''),
  });

  locations: ILocation[];

  isFilteredVehicles = false; // Ideally we should have this on by default
  isFiltered = false; // Ideally we should have this on by default

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private usersFacade: UsersFacade,
    private rolesFacade: RolesFacade,
    private breadcrumbService: BreadcrumbService,
    private helperService: HelperFunctionsService,
    private locationService: LocationsService
  ) {
    const { entityId, userId } = this.route.snapshot.params;
    this.entityId = entityId; // 'Supplier Id'
    this.userId = userId;

    this.breadcrumbService.setItems([
      { label: 'Users', url: '/users' },
      { label: 'Loading User...' },
    ]);
  }

  ngOnInit() {
    this.loggedInUser$ = this.store.select(getUser); // currently logged in user
    this.userEntity$ = this.store.select(getSelectedUser); // get the current user

    this.loading$ = combineLatest([
      this.usersFacade.loaded$,
      this.rolesFacade.isApplyingRoles$,
    ]).pipe(map(([loaded, isApplyingRoles]) => isApplyingRoles || !loaded));

    // Dispatch the action to fetch user entity
    this.usersFacade.dispatch(
      getUserEntity({ entityId: this.entityId, userId: this.userId })
    );

    // Set breadcrumb and update form group when the user entity is fetched
    this.userEntity$
      .pipe(
        takeUntilDestroyed(this.destroyRef), // it gets destroyed before it even loads... not sure whats going on
        tap((userEntity) => {
          this.breadcrumbService.setItems([
            { label: 'Users', url: '/users' },
            { label: 'User Details' },
          ]);
          this.updateFormGroup.patchValue({ ...userEntity });
        })
      )
      .subscribe((x) => {
        this.breadcrumbService.setItems([
          { label: 'Users', url: '/users' },
          {
            label: x?.user?.firstName + ' ' + x?.user?.surname || 'Loading...',
          },
        ]);
      });
  }

  handleTabChange(e) {
    if (e.index === 1) {
      this.loadPermissionsTab();
    }
  }

  loadPermissionsTab() {
    //Lets get all locations
    if (!this.locationService.isLoadedAllLocations(this.entityId)) {
      // Get the first search result then loop the rest with the load all locations command.
      // For "ease of use" the stake holders will like to see all locations so they can easily assign permissions, this goes against best practices but this is what we need to do.
      this.locationService
        .searchLocations(this.entityId)
        .pipe(
          switchMap((_) => this.locationService.loadAllLocations(this.entityId))
        )
        .subscribe((x) => {
          this.locations = x.items;
          this.processPermissions();
        });
    } else {
      this.locations = this.locationService.getLoadedLocations.items;
      this.processPermissions();
    }
  }

  processPermissions() {
    // Get Roles

    this.rolesFacade.dispatch(
      loadRoles({
        target:
          // tslint:disable-next-line:no-bitwise
          RoleAuthTarget.Entity |
          RoleAuthTarget.Location |
          RoleAuthTarget.Vehicle,
      })
    );
    this.rolesFacade.dispatch(loadAssignedAdminRoles({ userId: this.userId }));

    this.locationRoleOptions$ = this.store.select(getLocationRoles);
    this.entityRoleOptions$ = this.store.select(getEntityRoles);
    this.vehicleRoleOptions$ = this.store.select(getVehicleRoles);

    combineLatest([
      this.userEntity$.pipe(
        distinctUntilChanged(
          (previous, current) => previous.userId !== current.userId
        )
      ),
      this.locationRoleOptions$.pipe(
        distinctUntilChanged((previous, current) =>
          _.isEqual(previous, current)
        )
      ),
      this.vehicleRoleOptions$.pipe(
        distinctUntilChanged((previous, current) =>
          _.isEqual(previous, current)
        )
      ),
      this.entityRoleOptions$.pipe(
        distinctUntilChanged((previous, current) =>
          _.isEqual(previous, current)
        )
      ),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ([
          userEntity,
          locationRoleOptions,
          vehicleRoleOptions,
          entityRoleOptions,
        ]) => {
          // Here, you can access the values emitted by each observable
          // This block will only be reached when all observables have emitted at least once
          // Perform any necessary logic with the emitted values
          this.assignedLocationRolesDiff = [];

          // LETS PROCESS LOCATIONS
          const physicalLocations = this.locations.filter(
            (location) => location.type === 'Physical'
          );
          this.assignedLocationRolesDiff = this._mapLocationRoleAssignments(
            physicalLocations,
            locationRoleOptions,
            userEntity?.locationRoles
          );

          this.originalAssignedLocationRolesDiff =
            this.assignedLocationRolesDiff;

          // LETS PROCESS VEHICLES
          this.assignedVehicleRolesDiff = [];

          const vehicles = this.locations.filter(
            (location) => location.type === 'Vehicle'
          );
          this.assignedVehicleRolesDiff = this._mapLocationRoleAssignments(
            vehicles,
            vehicleRoleOptions,
            userEntity?.vehicleRoles
          );

          this.originalAssignedVehicleRolesDiff = this.assignedVehicleRolesDiff;

          this.assignedAdminRolesDiff = [];
          this.assignedCustomAdminRoles = [];
          for (const role of entityRoleOptions) {
            const isAssigned =
              userEntity.entityRoles.filter((x) =>
                x.roles.some((r) => r.id === role.id)
              ).length > 0;
            (role.entityId != null
              ? this.assignedCustomAdminRoles
              : this.assignedAdminRolesDiff
            ).push({
              id: role.id,
              title: role.name,
              assigned: isAssigned,
            });
          }
        }
      );
    // TODO Pagination would be nice...
    // Alternatively a show all and show only what there is
  }

  private _mapLocationRoleAssignments(
    locations: ILocation[],
    roles: IRole[],
    assignedRoles: IGenericRole[]
  ) {
    return locations.map((location) => {
      const mappedRoles = roles.map((role) => {
        let included = false;
        if (assignedRoles !== null) {
          const cvRoles = assignedRoles.filter(
            (i) => i.targetId === location.id
          );
          if (cvRoles.length > 0 && cvRoles[0].roles.length > 0)
            included =
              cvRoles[0].roles.filter((f) => f.id === role.id).length > 0;
        }
        return {
          id: role.id,
          title: role.name,
          assigned: included,
        };
      });

      return {
        id: location.id,
        title: location.name,
        roles: JSON.parse(JSON.stringify(mappedRoles)),
      };
    });
  }

  // Method to toggle between the original and filtered states

  toggleEmptyState() {
    if (this.isFiltered) {
      // Switching from filtered state to original state
      this.assignedLocationRolesDiff = [
        ...this.originalAssignedLocationRolesDiff,
      ];
    } else {
      // Switching from original state to filtered state
      this.assignedLocationRolesDiff = this.assignedLocationRolesDiff.filter(
        (location) => {
          return location.roles.some((role) => role.assigned);
        }
      );
    }
    this.isFiltered = !this.isFiltered;
  }

  toggleVehicleEmptyState() {
    if (this.isFilteredVehicles) {
      this.assignedVehicleRolesDiff = [
        ...this.originalAssignedVehicleRolesDiff,
      ];
    } else {
      this.assignedVehicleRolesDiff = this.assignedVehicleRolesDiff.filter(
        (vehicle) => {
          return vehicle.roles.some((role) => role.assigned);
        }
      );
    }
    this.isFilteredVehicles = !this.isFilteredVehicles;
  }

  // Perform Details Update
  onSubmitUpdateDetails() {
    const values = this.helperService.sanitizeParams(
      this.updateFormGroup.value
    );
    if (!Object.keys(values).length) return;

    this.usersFacade.dispatch(
      updateUserEntity({
        entityId: this.entityId,
        userId: this.userId,
        data: this.updateFormGroup.value,
      })
    );
  }

  loadLocations() {
    // maybe we should prompt for are you sure as this could get quite hectic.
    this.locationService.loadAllLocations(this.entityId);
  }

  // Perform Permissions Update
  onSubmitUpdatePermissions() {
    // Admin Request
    const adminRequest = [
      ...this.assignedAdminRolesDiff,
      ...this.assignedCustomAdminRoles,
    ]
      .map((m) =>
        m.assigned ? { roleId: m.id, targetId: this.entityId } : null
      )
      .filter((el) => el !== null)
      .map((p) => p);

    // Location Permissions Request
    const locationRequest: {
      roleId: string;
      targetId: string;
    }[] = this._constructRoleViewModelRequest(this.assignedLocationRolesDiff);

    const vehicleRequest = this._constructRoleViewModelRequest(
      this.assignedVehicleRolesDiff
    );

    this.rolesFacade.dispatch(
      applyUserRoles({
        userId: this.userId,
        roles: [...adminRequest, ...locationRequest, ...vehicleRequest],
      })
    );
  }

  private _constructRoleViewModelRequest(viewModel: IRoleSetViewModel[]) {
    let request: { roleId: string; targetId: string }[] = [];
    for (const { roles, id } of viewModel) {
      request = [
        ...request,
        ...roles
          .map((m) => (m.assigned ? { roleId: m.id, targetId: id } : null))
          .filter((el) => el !== null)
          .map((p) => p),
      ];
    }

    return request;
  }

  catch(error) {
    alert('Something went wrong whilst saving, try again');
    console.error('An error occurred during dispatch:', error);
  }
}
